import React, { useRef, useEffect, useCallback, forwardRef } from "react";
import TextField from "@mui/material/TextField";
import { useRecoilValue } from "recoil";
import { platformState } from "../global-state";

/**
 * MTextField - A Material-UI TextField wrapper with mobile keyboard optimization
 *
 * This component addresses mobile keyboard interaction issues by:
 * - Using enterKeyHint: 'done' to show a "Done" button on mobile keyboards
 * - Enabling proper autocorrect, autocomplete, and spellcheck for better UX
 * - Using polling to detect value changes on mobile (for keyboard suggestions)
 * - Avoiding race conditions by managing internal state during typing
 * - Providing enhanced keyboard experience specifically for mobile platforms
 * - Maintaining full TextField interface compatibility
 *
 * @example
 * // Basic usage with mobile keyboard optimization
 * <MTextField
 *   value={value}
 *   onChange={handleChange}
 *   label="Enter text"
 *   fullWidth
 * />
 *
 * @example
 * // With custom onBlur behavior
 * <MTextField
 *   value={value}
 *   onChange={handleChange}
 *   onBlur={(e) => handleFinish(e.target.value)}
 *   label="Enter text"
 * />
 *
 * @example
 * // Disable mobile optimizations for specific cases
 * <MTextField
 *   value={value}
 *   onChange={handleChange}
 *   label="Enter text"
 * />
 *
 * @param {Object} props - All Material-UI TextField props plus additional options
 * @param {function} props.onChange - Standard onChange callback
 * @param {function} props.onBlur - Standard onBlur callback
 * @param {any} props.value - Controlled value
 * @param {number} props.pollingInterval - Polling interval in milliseconds (default: 150)
 * @param {React.Ref} ref - React ref forwarded to the TextField
 * @returns {React.Component} Enhanced TextField with mobile keyboard optimization
 */
const MTextField = forwardRef(
  (
    {
      onChange,
      onBlur,
      value,
      pollingInterval = 150, // Poll every 150ms
      inputProps = {},
      ...textFieldProps
    },
    ref
  ) => {
    const platform = useRecoilValue(platformState);
    const inputRef = useRef(null);
    const pollingTimerRef = useRef(null);
    const lastValueRef = useRef("");
    const isTypingRef = useRef(false);

    // Determine if mobile optimizations should be enabled
    const shouldOptimizeForMobile = platform?.source === "app";

    if (true) {
      return (
        <TextField
          {...textFieldProps}
          ref={ref}
          onChange={onChange}
          onBlur={onBlur}
          value={value}
          inputProps={inputProps}
        />
      );
    }

    // Separate refs for TextField component and input element
    const textFieldRef = useCallback(
      (node) => {
        if (ref) {
          if (typeof ref === "function") {
            ref(node);
          } else {
            ref.current = node;
          }
        }
      },
      [ref]
    );

    const inputElementRef = useCallback((node) => {
      inputRef.current = node;
    }, []);

    // Sync internal value with controlled value only when not typing
    useEffect(() => {
      if (!isTypingRef.current && value !== undefined && value != null) {
        const newValue = value || "";
        lastValueRef.current = newValue;
        // Also refresh the TextField by updating the input element directly
        if (inputRef.current && inputRef.current.value !== newValue) {
          inputRef.current.value = newValue;
        }
      }
    }, [value]);

    // Polling function to detect value changes
    const checkValueChange = useCallback(() => {
      if (!inputRef.current) return;

      const currentValue = inputRef.current.value || "";
      const lastValue = lastValueRef.current;

      if (currentValue !== lastValue) {
        lastValueRef.current = currentValue;
        const syntheticEvent = {
          target: {
            value: currentValue,
            name: inputRef.current.name,
          },
          currentTarget: inputRef.current,
          type: "change",
          isSynthetic: true, // Flag to identify polling-generated events
        };
        if (onChange) {
          console.debug("polling detected change", currentValue);
          onChange(syntheticEvent);
        }
      }
    }, [onChange]);

    // Start polling when field is focused (mobile only)
    const startPolling = useCallback(() => {
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
      }
      pollingTimerRef.current = setInterval(checkValueChange, pollingInterval);
    }, [checkValueChange, pollingInterval]);

    // Stop polling
    const stopPolling = useCallback(() => {
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    }, []);

    // Enhanced blur handler
    const handleBlur = useCallback(
      (event) => {
        isTypingRef.current = false;
        stopPolling();
        setTimeout(() => {
          checkValueChange();
        }, 0);
        if (onBlur) {
          console.debug("handleBlur", event.target.value);
          onBlur(event);
        }
      },
      [stopPolling, checkValueChange, onBlur]
    );

    // Enhanced focus handler with polling setup
    const handleFocus = useCallback(
      (event) => {
        isTypingRef.current = true;
        startPolling();
        if (textFieldProps.onFocus) {
          textFieldProps.onFocus(event);
        }
      },
      [startPolling, textFieldProps.onFocus]
    );

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        stopPolling();
      };
    }, [stopPolling]);

    // Prepare mobile-optimized input props
    const mobileOptimizedInputProps = shouldOptimizeForMobile
      ? {
          enterKeyHint: "done", // Show "Done" button on mobile keyboards
          autoCorrect: "on", // Enable auto-correction for better UX
          autoComplete: "on", // Enable auto-completion
          spellCheck: true, // Enable spell checking
          ...inputProps, // Allow override of default mobile props
        }
      : inputProps;

    return (
      <TextField
        {...textFieldProps}
        ref={textFieldRef}
        defaultValue={value} // Always use valid value
        onFocus={handleFocus}
        onBlur={handleBlur}
        inputRef={inputElementRef}
        inputProps={mobileOptimizedInputProps}
      />
    );
  }
);

MTextField.displayName = "MTextField";

export default MTextField;
